import React, { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  MessageCircle, 
  Send,
  FileText,
  Calculator,
  Users,
  Award
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    quantity: '',
    message: '',
    urgent: false
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Créer le message WhatsApp
    const whatsappMessage = `
Bonjour DigiPrint,

Je souhaite obtenir un devis pour :

👤 Nom : ${formData.name}
📧 Email : ${formData.email}
📱 Téléphone : ${formData.phone}

📋 Service demandé : ${formData.service}
📊 Quantité : ${formData.quantity}

💬 Message :
${formData.message}

${formData.urgent ? '⚡ URGENT - Besoin rapide' : ''}

Merci !
    `.trim();

    const whatsappUrl = `https://wa.me/2250787635341?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  const services = [
    "Cartes de visite",
    "Flyers & Brochures", 
    "Affiches & Posters",
    "Reliure & Finition",
    "Impression Offset",
    "Impression Numérique",
    "Autre (préciser dans le message)"
  ];

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Téléphone",
      info: "+2250787635341",
      action: "tel:+2250787635341",
      actionText: "Appeler maintenant"
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "WhatsApp",
      info: "+2250787635341",
      action: "https://wa.me/2250787635341",
      actionText: "Chat WhatsApp"
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: "Email",
      info: "infos@digiprintnumerique.com",
      action: "mailto:infos@digiprintnumerique.com",
      actionText: "Envoyer un email"
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: "Adresse",
      info: "DIGIPRINT SARL Cocody II Plateaux Vallons,Rue des Jardins en face de la Station Corlay 26 BP 263 Abidjan 26",
      action: "https://maps.google.com",
      actionText: "Voir sur la carte"
    }
  ];

  const quickActions = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Devis express",
      description: "Obtenez un devis personnalisé en quelques clics",
      action: "#devis-form"
    },
    {
      icon: <Calculator className="w-8 h-8" />,
      title: "Calculateur de prix",
      description: "Estimez rapidement le coût de votre projet",
      action: "https://wa.me/2250787635341?text=Bonjour,%20j'aimerais%20un%20calcul%20rapide%20de%20prix"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Consultation gratuite",
      description: "Échangeons sur vos besoins d'impression",
      action: "tel:+2250787635341"
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="hero-gradient text-white py-16 flex items-center justify-center">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#e91d61' }}>
            Contactez-nous pour votre Projet
          </h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto" style={{ color: '#333333' }}>
            Notre équipe d'experts est à votre disposition pour vous conseiller et réaliser 
            vos projets d'impression avec la plus haute qualité à Abidjan.
          </p>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Contactez-nous rapidement
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {quickActions.map((action, index) => (
              <a
                key={index}
                href={action.action}
                className="bg-blue-50 hover:bg-blue-100 p-6 rounded-xl text-center transition-colors service-card-hover"
              >
                <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-white">
                  {action.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{action.title}</h3>
                <p className="text-gray-600">{action.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Main Contact Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6" id="devis-form">
                Demander un Devis Gratuit
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nom complet *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Votre nom complet"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="+225 XX XX XX XX XX"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Service souhaité *
                    </label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Sélectionner un service</option>
                      {services.map((service, index) => (
                        <option key={index} value={service}>{service}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Quantité estimée
                    </label>
                    <input
                      type="text"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Ex: 500 cartes, 1000 flyers..."
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Détails du projet *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Décrivez votre projet : format, couleurs, finitions souhaitées, délais..."
                    required
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="urgent"
                    checked={formData.urgent}
                    onChange={handleInputChange}
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                  />
                  <label className="ml-2 text-sm text-gray-700">
                    Projet urgent (moins de 48h)
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                >
                  <Send className="w-5 h-5" />
                  <span>Envoyer la demande via WhatsApp</span>
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  Nos Coordonnées
                </h2>
                <div className="space-y-6">
                  {contactInfo.map((contact, index) => (
                    <div key={index} className="flex items-start space-x-4">
                      <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
                        {contact.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{contact.title}</h3>
                        <p className="text-gray-600 mb-2">{contact.info}</p>
                        <a
                          href={contact.action}
                          target={contact.action.startsWith('http') ? '_blank' : undefined}
                          rel={contact.action.startsWith('http') ? 'noopener noreferrer' : undefined}
                          className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                        >
                          {contact.actionText}
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Horaires */}
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center space-x-3 mb-6">
                  <div className="bg-blue-100 p-3 rounded-lg text-blue-600">
                    <Clock className="w-6 h-6" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Horaires d'ouverture</h2>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lundi - Vendredi</span>
                    <span className="font-medium">8h30 - 17h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Samedi</span>
                    <span className="font-medium">8h00 - 13h00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dimanche</span>
                    <span className="text-red-600 font-medium">Fermé</span>
                  </div>
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    <Award className="w-4 h-4 inline mr-2" />
                    Service express disponible 24h/24 pour les commandes urgentes
                  </p>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
                <div className="flex items-center">
                  <MessageCircle className="w-8 h-8 text-green-600" />
                  <div className="ml-3">
                    <h3 className="text-lg font-semibold text-green-800">
                      Besoin urgent ?
                    </h3>
                    <p className="text-green-700 mb-3">
                      WhatsApp disponible 24h/24 pour vos urgences
                    </p>
                    <a
                      href="https://wa.me/2250787635341?text=🚨%20URGENT%20-%20J'ai%20besoin%20d'aide%20pour%20un%20projet%20d'impression"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg transition-colors inline-block"
                    >
                      Contact d'urgence
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Questions Fréquentes
          </h2>
          <div className="max-w-3xl mx-auto space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">
                Quels sont vos délais de livraison ?
              </h3>
              <p className="text-gray-600">
                Nos délais standards sont de 24-48h pour la plupart des services. 
                Pour les gros tirages offset, comptez 3-5 jours. Service express disponible.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">
                Acceptez-vous les paiements par mobile money ?
              </h3>
              <p className="text-gray-600">
                Oui, nous acceptons Orange Money, MTN Money, Wave, ainsi que 
                les paiements en espèces et par chèque.
              </p>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">
                Proposez-vous des services de conception graphique ?
              </h3>
              <p className="text-gray-600">
                Oui, notre équipe de graphistes peut créer ou retoucher vos visuels. 
                Service de création inclus dans nos tarifs.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;